// Encryption helpers (Web Crypto, AES-GCM).
//
// Your data is encrypted with a random "data key". That key is itself stored
// twice, each copy locked with a different key:
//   - one derived from your PIN (PBKDF2)
//   - one derived from your Face ID passkey (WebAuthn PRF extension)
// Either one can unlock the data key; neither is ever stored in plain form.

const enc = new TextEncoder();
const dec = new TextDecoder();

export const PIN_ITERATIONS = 600000;

export const randomBytes = (n) => crypto.getRandomValues(new Uint8Array(n));

export function toB64(buf) {
  const bytes = new Uint8Array(buf);
  let s = "";
  for (let i = 0; i < bytes.length; i += 0x8000) {
    s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(s);
}

export const fromB64 = (s) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

export const aesKey = (raw) =>
  crypto.subtle.importKey("raw", raw, "AES-GCM", false, ["encrypt", "decrypt"]);

export async function keyFromPin(pin, salt, iterations = PIN_ITERATIONS) {
  const base = await crypto.subtle.importKey("raw", enc.encode(pin), "PBKDF2", false, [
    "deriveBits",
  ]);
  const bits = await crypto.subtle.deriveBits(
    { name: "PBKDF2", salt, iterations, hash: "SHA-256" },
    base,
    256
  );
  return aesKey(bits);
}

export async function keyFromPrf(prfOutput) {
  const base = await crypto.subtle.importKey("raw", prfOutput, "HKDF", false, [
    "deriveBits",
  ]);
  const bits = await crypto.subtle.deriveBits(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: new Uint8Array(32),
      info: enc.encode("nest-face-id"),
    },
    base,
    256
  );
  return aesKey(bits);
}

export async function seal(key, bytes) {
  const iv = randomBytes(12);
  const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, bytes);
  return { iv: toB64(iv), ct: toB64(ct) };
}

export async function unseal(key, box) {
  const pt = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: fromB64(box.iv) },
    key,
    fromB64(box.ct)
  );
  return new Uint8Array(pt);
}

export const sealJSON = (key, obj) => seal(key, enc.encode(JSON.stringify(obj)));
export const unsealJSON = async (key, box) => JSON.parse(dec.decode(await unseal(key, box)));
